/**
 * The project page's right pane: the editor while one is open (`+ New`, or
 * a definition's Edit), else the selected definition (`?def=`) once the
 * list has that entry, else a hint.
 *
 * A withdraw's outcome ("Withdrew !N …", with why a branch was left in
 * place) or its refusal (a 409: another MR now, or none) is shown above
 * it: the page keeps it until the next selection or confirm, since a
 * withdrawn proposed definition leaves the list, and the pane with it, and
 * the refetch a refusal triggers replaces or drops the pane's Withdraw.
 */

import type { ReactElement } from 'react';
import { DefinitionDetail } from './DefinitionDetail';
import { DefinitionEditor } from './DefinitionEditor';
import type { DefinitionEditStart, WithdrawOutcome } from './DefinitionActions';
import { setupBlockReason } from '../utils/setup';
import type { PanelResponse } from '../types/projectPanel.types';

/** What to open the editor on: a new definition, or an edit from the text Save replaces. */
export type EditorRequest = { kind: 'new' } | { kind: 'edit'; start: DefinitionEditStart };

/** An open editor; `seq` counts openings, so a reopened editor starts afresh. */
export type EditorTarget = EditorRequest & { seq: number };

/** A withdraw's outcome, and the definition it was for. */
export interface Withdrawn extends WithdrawOutcome {
  fileName: string;
}

interface DefinitionPaneProps {
  projectId: string;
  panel: PanelResponse;
  selected: string | null;
  editing: EditorTarget | null;
  withdrawn: Withdrawn | null;
  onEdit: (start: DefinitionEditStart) => void;
  onCloseEditor: () => void;
  onSaved: (name: string) => void;
  onWithdrawn: (withdrawn: Withdrawn | null) => void;
  /** The editor's text differs from what it opened with (the page asks before discarding it). */
  onDirtyChange?: (dirty: boolean) => void;
  /** The viewer opened this pane (a selection, `+ New`, Edit, Close): focus its heading. */
  focusPane?: boolean;
}

function Selected(props: DefinitionPaneProps & { selected: string }): ReactElement {
  const { panel, selected } = props;
  const found = [...panel.definitions, ...panel.proposed].find((d) => d.file_name === selected);
  if (found === undefined) return <p className="project-page__hint">No definition {selected} in this project.</p>;
  return (
    <DefinitionDetail
      key={selected}
      projectId={props.projectId}
      fileName={selected}
      summary={found}
      focusOnShow={props.focusPane === true}
      projectWebUrl={panel.web_url}
      defaultBranch={panel.default_branch}
      onEdit={props.onEdit}
      editBlocked={setupBlockReason(panel.setup)}
      onWithdrawOutcome={(outcome) => {
        props.onWithdrawn(outcome === null ? null : { ...outcome, fileName: selected });
      }}
    />
  );
}

export function DefinitionPane(props: DefinitionPaneProps): ReactElement {
  const { editing, withdrawn, selected } = props;
  if (editing !== null) {
    const start = editing.kind === 'edit' ? editing.start : null;
    return (
      <DefinitionEditor
        key={editing.seq}
        projectId={props.projectId}
        name={start?.name ?? null}
        initialContent={start?.content ?? null}
        projectWebUrl={props.panel.web_url}
        existingNames={[...props.panel.definitions, ...props.panel.proposed].map((d) => d.name)}
        onSaved={(_mrUrl, name) => { props.onSaved(name); }}
        onClose={props.onCloseEditor}
        saveBlocked={setupBlockReason(props.panel.setup)}
        focusOnShow={props.focusPane === true}
        {...(props.onDirtyChange !== undefined ? { onDirtyChange: props.onDirtyChange } : {})}
      />
    );
  }
  return (
    <>
      {withdrawn !== null && withdrawn.fileName === selected && (
        withdrawn.failed ? (
          <div className="error-message project-page__withdrawn" role="alert">{withdrawn.message}</div>
        ) : (
          <p className="project-page__withdrawn" role="status">{withdrawn.message}</p>
        )
      )}
      {selected === null ? (
        <p className="project-page__hint">Select a definition to see it and its runs.</p>
      ) : (
        <Selected {...props} selected={selected} />
      )}
    </>
  );
}
